import { useState, useRef, useEffect } from "react";
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import * as SelectPrimitive from "@radix-ui/react-select";
import * as LabelPrimitive from "@radix-ui/react-label";
import {
  Upload,
  X,
  Check,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { snagCategories, snagSubcategories } from "./snagsData";
import { ProjectApi } from "api/projects/service";
import { useAlert } from "components/UI_components/Alert";

function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-sm-primary text-primary-foreground hover:bg-sm-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

const Button = React.forwardRef((props, ref) => {
  const { className, variant, size, asChild = false, ...rest } = props;
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      ref={ref}
      className={cn(buttonVariants({ variant, size, className }))}
      {...rest}
    />
  );
});
Button.displayName = "Button";

const Input = React.forwardRef((props, ref) => {
  const { className, type, ...rest } = props;
  return (
    <input
      ref={ref}
      type={type}
      className={cn(
        "flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...rest}
    />
  );
});
Input.displayName = "Input";

const Textarea = React.forwardRef((props, ref) => {
  const { className, ...rest } = props;
  return (
    <textarea
      ref={ref}
      className={cn(
        "flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...rest}
    />
  );
});
Textarea.displayName = "Textarea";

const labelVariants = cva(
  "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
);

const Label = React.forwardRef(({ className, ...props }, ref) => (
  <LabelPrimitive.Root
    ref={ref}
    className={cn(labelVariants(), className)}
    {...props}
  />
));
Label.displayName = "Label";

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "border-transparent bg-sm-primary text-primary-foreground",
        secondary: "border-transparent bg-secondary text-secondary-foreground",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground",
        outline: "text-foreground",
      },
    },
    defaultVariants: { variant: "default" },
  },
);

function Badge(props) {
  const { className, variant, ...rest } = props;
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...rest} />
  );
}

const Select = SelectPrimitive.Root;
const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef(
  ({ className, children, ...props }, ref) => (
    <SelectPrimitive.Trigger
      ref={ref}
      className={cn(
        "flex h-11 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDown className="h-4 w-4 opacity-50" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  ),
);
SelectTrigger.displayName = "SelectTrigger";

const SelectScrollUpButton = React.forwardRef(
  ({ className, ...props }, ref) => (
    <SelectPrimitive.ScrollUpButton
      ref={ref}
      className={cn(
        "flex cursor-default items-center justify-center py-1",
        className,
      )}
      {...props}
    >
      <ChevronUp className="h-4 w-4" />
    </SelectPrimitive.ScrollUpButton>
  ),
);
SelectScrollUpButton.displayName = "SelectScrollUpButton";

const SelectScrollDownButton = React.forwardRef(
  ({ className, ...props }, ref) => (
    <SelectPrimitive.ScrollDownButton
      ref={ref}
      className={cn(
        "flex cursor-default items-center justify-center py-1",
        className,
      )}
      {...props}
    >
      <ChevronDown className="h-4 w-4" />
    </SelectPrimitive.ScrollDownButton>
  ),
);
SelectScrollDownButton.displayName = "SelectScrollDownButton";

const SelectContent = React.forwardRef(
  ({ className, children, position = "popper", ...props }, ref) => (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        position={position}
        sideOffset={6}
        collisionPadding={12}
        className={cn(
          "z-[9999] max-h-72 min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-md border border-sm-border bg-background text-popover-foreground shadow-xl",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          "data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2",
          position === "popper" &&
            "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
          className,
        )}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport className="p-1">
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  ),
);
SelectContent.displayName = "SelectContent";

const SelectItem = React.forwardRef(
  ({ className, children, ...props }, ref) => (
    <SelectPrimitive.Item
      ref={ref}
      className={cn(
        "relative flex w-full cursor-default select-none items-center rounded-sm py-2 pl-8 pr-2 text-sm outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 focus:bg-accent focus:text-accent-foreground",
        className,
      )}
      {...props}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="h-4 w-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  ),
);
SelectItem.displayName = "SelectItem";

const Sheet = SheetPrimitive.Root;
const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/70 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
SheetOverlay.displayName = "SheetOverlay";

const sheetVariants = cva(
  "fixed z-50 gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b",
        bottom: "inset-x-0 bottom-0 border-t",
        left: "inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm",
        right: "inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm",
      },
    },
    defaultVariants: { side: "right" },
  },
);

const SheetContent = React.forwardRef(
  ({ side = "right", className, children, ...props }, ref) => (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        ref={ref}
        className={cn(sheetVariants({ side }), className)}
        {...props}
      >
        {children}
      </SheetPrimitive.Content>
    </SheetPortal>
  ),
);
SheetContent.displayName = "SheetContent";

const SheetHeader = ({ className, ...props }) => (
  <div
    className={cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      className,
    )}
    {...props}
  />
);
SheetHeader.displayName = "SheetHeader";

const SheetTitle = React.forwardRef(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold text-foreground", className)}
    {...props}
  />
));
SheetTitle.displayName = "SheetTitle";
const SheetDescription = React.forwardRef(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
SheetDescription.displayName = "SheetDescription";

const API_BASE_URL = (process.env.REACT_APP_BASE_URL || "").replace(/\/$/, "");

const PRIORITY_MAP = {
  Critical: 0,
  High: 1,
  Medium: 2,
  Low: 3,
};

const OTHER_CATEGORY_VALUE = "Other";

const unwrapList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const normalizeProject = (p, i) => ({
  id: String(p?.id ?? p?.project_id ?? p?.site_id ?? i + 1),
  name: p?.name ?? p?.project_name ?? p?.site_name ?? `Project ${i + 1}`,
});

const normalizeMember = (m, i) => ({
  id: String(m?.id ?? m?.user?.id ?? m?.member_id ?? i + 1),
  label:
    `${m?.first_name || m?.user?.first_name || ""} ${m?.last_name || m?.user?.last_name || ""}`.trim() ||
    m?.full_name ||
    m?.name ||
    m?.user?.full_name ||
    m?.user?.name ||
    m?.email ||
    m?.user?.email ||
    `Member ${i + 1}`,
});

async function readErrorMessage(response, fallback) {
  const text = await response.text().catch(() => "");
  if (!text) return fallback;
  try {
    const parsed = JSON.parse(text);
    return (
      parsed?.detail ||
      parsed?.message ||
      Object.values(parsed || {})?.[0]?.[0] ||
      fallback
    );
  } catch {
    return fallback;
  }
}

async function createSnagAPI({
  orgId,
  siteId,
  token,
  projectId,
  title,
  description,
  remarks,
  issueImage,
  location,
  category,
  subcategory,
  priority,
  expectedClosure,
  status = "Draft",
}) {
  if (!API_BASE_URL) throw new Error("REACT_APP_BASE_URL is missing.");
  if (!token) throw new Error("Auth token is missing.");
  if (!orgId) throw new Error("Organization ID is missing.");

  const resolvedSiteId = siteId || projectId;
  if (!resolvedSiteId) throw new Error("Site ID is missing.");

  const submitCreate = async (statusValue) => {
    const formData = new FormData();
    formData.append("title", title);
    formData.append("description", description || remarks || "");
    formData.append("location", location || "");
    formData.append("category", category);
    if (subcategory) formData.append("subcategory", subcategory);
    formData.append("priority", String(PRIORITY_MAP[priority] ?? 2));
    formData.append("status", statusValue);
    if (expectedClosure) {
      formData.append("expected_closure_date", expectedClosure);
    }
    formData.append("site", String(projectId));
    if (projectId && String(projectId) !== String(resolvedSiteId)) {
      formData.append("project", String(projectId));
    }
    if (issueImage) formData.append("issue_image", issueImage);

    const response = await fetch(
      `${API_BASE_URL}/snag-management/org/${orgId}/snags/create/`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      },
    );

    if (!response.ok) {
      const message = await readErrorMessage(
        response,
        `Create snag failed (${response.status})`,
      );
      throw new Error(message);
    }

    return response.json().catch(() => ({}));
  };

  return submitCreate(status);
}

async function assignSnagMembersAPI({ orgId, snagId, token, memberIds }) {
  if (!orgId || !snagId || !token) return;
  if (!Array.isArray(memberIds) || memberIds.length === 0) return;

  const response = await fetch(
    `${API_BASE_URL}/snag-management/org/${orgId}/snags/${snagId}/assign/`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        member_ids: memberIds
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id)),
      }),
    },
  );

  if (!response.ok) {
    const message = await readErrorMessage(
      response,
      `Assign members failed (${response.status})`,
    );
    throw new Error(message);
  }
}

async function setSnagOpenAPI({ orgId, snagId, token }) {
  if (!orgId || !snagId || !token) return;

  const updatePayload = { status: "Open" };
  const updateEndpoints = [
    `/snag-management/org/${orgId}/snags/${snagId}/`,
    `/snag-management/org/${orgId}/snags/${snagId}/update/`,
  ];

  for (const endpoint of updateEndpoints) {
    for (const method of ["PATCH", "PUT"]) {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updatePayload),
      });
      if (response.ok) return;
    }
  }

  const endpoints = ["open", "reopen"];
  let lastError = "";

  for (const actionPath of endpoints) {
    const response = await fetch(
      `${API_BASE_URL}/snag-management/org/${orgId}/snags/${snagId}/${actionPath}/`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ comment: "" }),
      },
    );

    if (response.ok) return;
    lastError = await readErrorMessage(
      response,
      `Set Open failed (${response.status})`,
    );
  }

  throw new Error(lastError || "Set Open failed");
}

export function CreateSnagDrawer({
  open,
  onOpenChange,
  onSnagCreated,
  token,
  orgId,
  siteId,
}) {
  const { addAlert } = useAlert();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [project, setProject] = useState("");
  const [location, setLocation] = useState("");
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [customCategory, setCustomCategory] = useState("");
  const [customSubcategory, setCustomSubcategory] = useState("");
  const [priority, setPriority] = useState("Medium");
  const [assignees, setAssignees] = useState([]);
  const [approver, setApprover] = useState("");
  const [expectedClosure, setExpectedClosure] = useState("");
  const [issueImage, setIssueImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [projects, setProjects] = useState([]);
  const [members, setMembers] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const fileInputRef = useRef(null);

  const isOtherCategory = category === OTHER_CATEGORY_VALUE;
  const resolvedCategory = isOtherCategory ? customCategory.trim() : category;
  const resolvedSubcategory = isOtherCategory
    ? customSubcategory.trim()
    : subcategory;
  const requiresCustomCategory = isOtherCategory && !resolvedCategory;
  const availableSubcategories =
    snagSubcategories[resolvedCategory] || snagSubcategories[category] || [];

  useEffect(() => {
    if (!open || !orgId) return;

    const loadOptions = async () => {
      setLoadingOptions(true);
      setError(null);

      try {
        const [projectsData, membersRes] = await Promise.all([
          ProjectApi.listSitesByOrganization(orgId),
          token && API_BASE_URL
            ? fetch(`${API_BASE_URL}/org/organization/${orgId}/members/`, {
                method: "GET",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
              })
            : Promise.resolve(null),
        ]);

        const membersData = membersRes
          ? await membersRes.json().catch(() => ({}))
          : [];
        if (membersRes && !membersRes.ok) {
          throw new Error(
            membersData?.detail ||
              membersData?.message ||
              `Failed to fetch organization members (${membersRes.status})`,
          );
        }

        setProjects(unwrapList(projectsData).map(normalizeProject));
        setMembers(unwrapList(membersData).map(normalizeMember));
      } catch (e) {
        setProjects([]);
        setMembers([]);
        setError(e.message || "Failed to load projects/members");
      } finally {
        setLoadingOptions(false);
      }
    };

    loadOptions();
  }, [open, orgId, token]);

  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);

  const reset = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setTitle("");
    setDescription("");
    setProject("");
    setLocation("");
    setCategory("");
    setSubcategory("");
    setCustomCategory("");
    setCustomSubcategory("");
    setPriority("Medium");
    setAssignees([]);
    setApprover("");
    setExpectedClosure("");
    setIssueImage(null);
    setImagePreview(null);
    setError(null);
  };

  const handleImageChange = (file) => {
    if (!file) return;
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setIssueImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    if (!resolvedCategory) {
      setError(
        isOtherCategory ? "Please enter a custom category." : "Category is required.",
      );
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const memberIdsForAssign = approver
        ? Array.from(new Set([...assignees, approver]))
        : assignees;
      const shouldOpen =
        memberIdsForAssign.length > 0 && Boolean(expectedClosure);

      const newSnag = await createSnagAPI({
        orgId,
        siteId,
        token,
        projectId: project,
        title,
        description,
        issueImage,
        location,
        category: resolvedCategory,
        subcategory: resolvedSubcategory,
        priority,
        expectedClosure,
        status: shouldOpen ? "Open" : "Draft",
      });

      const createdSnagId = newSnag?.id || newSnag?.data?.id;

      await assignSnagMembersAPI({
        orgId,
        snagId: createdSnagId,
        token,
        memberIds: memberIdsForAssign,
      });

      // ← removed the redundant setSnagOpenAPI call

      onSnagCreated?.(newSnag);
      addAlert("Snag created successfully", "success");
      reset();
      onOpenChange(false);
    } catch (err) {
      addAlert(
        err.message || "Failed to create snag. Please try again.",
        "error",
      );
      setError(err.message || "Failed to create snag. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!resolvedCategory) {
      setError(
        isOtherCategory ? "Please enter a custom category." : "Category is required.",
      );
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const newSnag = await createSnagAPI({
        orgId,
        siteId,
        token,
        projectId: project,
        title,
        description,
        issueImage,
        location,
        category: resolvedCategory,
        subcategory: resolvedSubcategory,
        priority,
        expectedClosure,
        status: "Draft",
      });

      onSnagCreated?.(newSnag);
      addAlert("Draft snag created successfully", "success");
      reset();
      onOpenChange(false);
    } catch (err) {
      addAlert(
        err.message || "Failed to save draft snag. Please try again.",
        "error",
      );
      setError(err.message || "Failed to save draft snag. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <Sheet
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <SheetContent
        side="right"
        className="flex w-[520px] flex-col bg-background p-0 sm:max-w-[620px]"
      >
        <SheetHeader className="border-b flex flex-row justify-between items-center border-sm-border px-6 h-16 py-2 relative">
          <SheetTitle className="text-xl font-semibold text-primary-text">
            Create New Snag
          </SheetTitle>
          {/* <SheetDescription className="sr-only">
            Create a snag, select project, and assign members.
          </SheetDescription> */}
          {/* <p className="mt-1 text-sm text-text-gray">SNG-009</p> */}
          <SheetPrimitive.Close className=" rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-3">
            <div className="space-y-5">
              <div
                className="cursor-pointer rounded-xl border-2 border-dashed border-sm-border p-4 text-center transition hover:border-primary"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  handleImageChange(e.dataTransfer.files?.[0]);
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleImageChange(e.target.files?.[0])}
                />
                {imagePreview ? (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="mx-auto max-h-24 rounded-lg object-cover"
                    />
                    <button
                      type="button"
                      className="absolute right-0 top-0 rounded-full bg-dark-red p-1 text-white"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (imagePreview) URL.revokeObjectURL(imagePreview);
                        setIssueImage(null);
                        setImagePreview(null);
                      }}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="mx-auto mb-1 h-6 w-6 text-text-gray" />
                    <p className="text-sm font-medium text-primary-text">
                      Upload Issue Image
                    </p>
                    <p className="mt-1 text-xs text-text-gray">
                      Drag & drop or click
                    </p>
                  </>
                )}
              </div>

              <div>
                <Label className="text-sm text-text-gray">Title *</Label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="mt-1 h-11 border-sm-border bg-input-bg"
                />
              </div>

              <div>
                <Label className="text-sm text-text-gray">Description</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="mt-1 min-h-[120px] border-sm-border bg-input-bg"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm text-text-gray">Project *</Label>
                  <Select value={project} onValueChange={setProject}>
                    <SelectTrigger className="mt-1 h-11 border-sm-border bg-input-bg">
                      <SelectValue
                        placeholder={
                          loadingOptions
                            ? "Loading projects..."
                            : "Select project"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                        </SelectItem>
                      ))}
                      {!loadingOptions && projects.length === 0 && (
                        <SelectItem value="__no_projects__" disabled>
                          No projects found
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm text-text-gray">Location</Label>
                  <Input
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="mt-1 h-11 border-sm-border bg-input-bg"
                  />
                </div>
              </div>

              {error && (
                <div className="rounded-lg border border-dark-red/20 bg-dark-red/10 px-4 py-3 text-sm text-dark-red">
                  {error}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm text-text-gray">Category *</Label>
                  <Select
                    value={category}
                    onValueChange={(v) => {
                      setCategory(v);
                      setSubcategory("");
                      setCustomCategory("");
                      setCustomSubcategory("");
                    }}
                  >
                    <SelectTrigger className="mt-1 h-11 border-sm-border bg-input-bg">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {snagCategories.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {isOtherCategory && (
                    <Input
                      value={customCategory}
                      onChange={(e) => setCustomCategory(e.target.value)}
                      placeholder="Enter custom category"
                      className="mt-2 h-11 border-sm-border bg-input-bg"
                    />
                  )}
                </div>

                <div>
                  <Label className="text-sm text-text-gray">
                    Subcategory (Optional)
                  </Label>
                  {isOtherCategory ? (
                    <Input
                      value={customSubcategory}
                      onChange={(e) => setCustomSubcategory(e.target.value)}
                      placeholder="Enter custom subcategory"
                      className="mt-1 h-11 border-sm-border bg-input-bg"
                    />
                  ) : (
                    <Select
                      value={subcategory}
                      onValueChange={setSubcategory}
                      disabled={!category}
                    >
                      <SelectTrigger className="mt-1 h-11 border-sm-border bg-input-bg">
                        <SelectValue placeholder="Select subcategory" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableSubcategories.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-sm text-text-gray">Priority *</Label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger className="mt-1 h-11 border-sm-border bg-input-bg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {["Critical", "High", "Medium", "Low"].map((p) => (
                      <SelectItem key={p} value={p}>
                        {p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm text-text-gray">
                  Assign Members *
                </Label>
                <div className="mt-3 flex flex-wrap gap-2">
                  {members.map((m) => {
                    const active = assignees.includes(m.id);
                    return (
                      <Badge
                        key={m.id}
                        className={cn(
                          "cursor-pointer px-3 py-1.5 text-sm",
                          active
                            ? "bg-sm-primary text-white font-semibold"
                            : "border border-sm-border bg-background text-primary-text hover:bg-task-hover font-normal",
                        )}
                        onClick={() =>
                          setAssignees(
                            active
                              ? assignees.filter((a) => a !== m.id)
                              : [...assignees, m.id],
                          )
                        }
                      >
                        {m.label}
                      </Badge>
                    );
                  })}
                </div>
              </div>

              {/* <div>
                <Label className="text-sm text-text-gray">
                  Approver / Reviewer
                </Label>
                <Select value={approver} onValueChange={setApprover}>
                  <SelectTrigger className="mt-1 h-11 border-sm-border bg-input-bg">
                    <SelectValue placeholder="Select approver" />
                  </SelectTrigger>
                  <SelectContent>
                    {members.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.label}
                      </SelectItem>
                    ))}
                    {!loadingOptions && members.length === 0 && (
                      <SelectItem value="__no_members__" disabled>
                        No members found
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div> */}
              <div>
                <Label className="text-sm text-text-gray">
                  Expected Closure Date *
                </Label>
                <Input
                  type="date"
                  value={expectedClosure}
                  onChange={(e) => setExpectedClosure(e.target.value)}
                  className="mt-1 h-11 border-sm-border bg-input-bg text-sm"
                />
              </div>
              {error && (
                <div className="rounded-lg border border-dark-red/20 bg-dark-red/10 px-4 py-3 text-sm text-dark-red">
                  {error}
                </div>
              )}
            </div>
        </div>

        {/* Sticky footer */}
        <div className="border-t px-5 py-3 flex justify-between shrink-0 bg-background">
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            onClick={handleSaveDraft}
            disabled={
              isSubmitting ||
              !title ||
              !project ||
              !resolvedCategory ||
              requiresCustomCategory
            }
          >
            {isSubmitting ? "Saving..." : "Save as Draft"}
          </Button>
          <Button
            size="sm"
            className="h-8 text-xs text-white"
            onClick={handleSubmit}
            disabled={
              isSubmitting ||
              !title ||
              !project ||
              !resolvedCategory ||
              requiresCustomCategory ||
              (assignees.length === 0 && !approver) ||
              !expectedClosure
            }
          >
            {isSubmitting ? "Creating..." : "Create & Assign"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
